// Service Steady PWA — src/hooks/useGeolocation.ts — v2.0 — built 2026-06-04
//
// Wrapper around navigator.geolocation.getCurrentPosition with a 10s timeout
// and structured error reporting. Does NOT auto-prompt on mount — only fires
// when the consumer calls request(). UI affordance: a "Use my location"
// button inside LocationPickerModal.
//
// Browser permission UX: getCurrentPosition triggers the native iOS/Android
// permission prompt the first time. Subsequent calls reuse the cached
// permission. If the user denies, the GeolocationPositionError.code === 1
// (PERMISSION_DENIED) and we surface 'denied' to the caller.

import { useCallback, useState } from 'react';

const TIMEOUT_MS = 10_000;

export type GeolocationStatus = 'idle' | 'pending' | 'ok' | 'error';
export type GeolocationErrorCode =
  | 'unsupported'
  | 'denied'
  | 'unavailable'
  | 'timeout'
  | 'unknown';

export interface GeolocationResult {
  lat: number;
  lon: number;
  accuracy_m: number;
}

export function useGeolocation(): {
  status: GeolocationStatus;
  result: GeolocationResult | null;
  error: GeolocationErrorCode | null;
  request: () => Promise<GeolocationResult | null>;
} {
  const [status, setStatus] = useState<GeolocationStatus>('idle');
  const [result, setResult] = useState<GeolocationResult | null>(null);
  const [error, setError] = useState<GeolocationErrorCode | null>(null);

  const request = useCallback(async (): Promise<GeolocationResult | null> => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setStatus('error');
      setError('unsupported');
      return null;
    }
    setStatus('pending');
    setError(null);
    return new Promise<GeolocationResult | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const r: GeolocationResult = {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            accuracy_m: pos.coords.accuracy,
          };
          setResult(r);
          setStatus('ok');
          resolve(r);
        },
        (err) => {
          let code: GeolocationErrorCode = 'unknown';
          if (err.code === 1) code = 'denied';
          else if (err.code === 2) code = 'unavailable';
          else if (err.code === 3) code = 'timeout';
          setError(code);
          setStatus('error');
          resolve(null);
        },
        {
          timeout: TIMEOUT_MS,
          maximumAge: 10 * 60 * 1000, // 10 min — happily reuse a recent fix
          enableHighAccuracy: false,
        },
      );
    });
  }, []);

  return { status, result, error, request };
}
